import { gsap } from 'gsap';
import { computed, nextTick, ref, watch, type Ref } from 'vue';
import type { MeasureBound, Note } from '@/types';
import { computeSheetScrollPosition, getMeasureAtPoint, getMeasureBoundForIndex, getTrailingNoteSpeed } from '@/utils';
import { useInjectGameEngine } from './useGameEngine';
import { useInjectSheetMusic } from './useSheetMusic';
import { useSettings } from './useSettings';

type MultiRow = {
  rowGroup: number;
  x: number;
  y: number;
  width: number;
  height: number;
  measureStartIndex: number;
  measureEndIndex: number;
  noteStartMs: number;
  noteEndMs: number;
  soundEndMs: number;
  notes: Note[];
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

// Reserved band at the top of the viewport for the hit-text popup and combo
// counter (both pinned near the top): in multi-line mode the score fills the
// whole viewport instead of sitting in a thin centred strip, so without this
// the HUD text would land directly on top of playable notation. The active
// row is centred within the space *below* this band instead of the full
// viewport, so it's never covered — not even visually masked, genuinely clear.
const MULTI_HUD_GUTTER_PX = 96;
// The blur visually needs more room to taper off gradually than the gutter
// logically needs to reserve — a fade confined to exactly MULTI_HUD_GUTTER_PX
// still reads as a hard cutoff at its edge. This taller band is purely visual;
// centering/clamping still only ever reserves MULTI_HUD_GUTTER_PX.
export const MULTI_HUD_VISUAL_FADE_PX = Math.round(MULTI_HUD_GUTTER_PX * 1.15);

// In single-line mode the target line lives inside `.sheet-music-stage`,
// which is itself CSS-scaled by `singleScale` — its thickness/arrowheads
// scale for free as part of that transform. The multi-line marker below is
// positioned with explicit top/left/height instead (OSMD re-renders natively
// per row at the current zoom), so its stroke width and arrowhead size need
// to be scaled by hand to match.
const MULTI_MARKER_LINE_WIDTH_PX = 2;
const MULTI_MARKER_ARROW_HALF_WIDTH_PX = 8;
const MULTI_MARKER_ARROW_HEIGHT_PX = 12;
// Row bounds hug the noteheads themselves, so a marker sized to exactly
// `row.height` clips the arrowheads against the staff — pad it out a bit on
// both ends so the line visibly encloses the staffline instead.
const MULTI_MARKER_VERTICAL_MARGIN_PX = 24;

type MultiRowSheetMusicOptions = {
  /** Scrollable ancestor of the rendered sheet; multi-line mode scrolls this to follow playback. */
  sheetViewportEl: Ref<HTMLElement | undefined>;
  /** Shared measure geometry, recomputed by the parent whenever the sheet re-lays-out. */
  measureBoundsForScroll: Ref<MeasureBound[]>;
  /** Notes currently backing the rendered sheet (game notes once gameplay starts, else raw render notes). */
  activeRenderedNotes: () => Note[];
  /** CSS/OSMD-native zoom scale, shared with single-line mode. */
  multiScale: () => number;
  /** Gate click-to-seek the same way single-line mode does. */
  canSeek: () => boolean;
  /** Mirrors single-line mode's `emit('seek', ...)`. */
  onSeek: (data: { timeMs: number; wasPlaying: boolean }) => void;
  /** Parent's shared geometry refresh (re-measures notes/bounds for either mode). */
  refreshSheetGeometry: () => Promise<void>;
  /** Parent's shared game-notes/render-notes reconciliation + notehead repaint. */
  syncRenderedNotesIntoGameNotes: () => void;
};

/**
 * Everything specific to the scrolling-staffline ("multi-line") render mode:
 * grouping measures/notes into rows, the row-following target-line marker,
 * auto-follow-vs-manual-scroll tracking, and the single/multi mode toggle.
 * Single-line mode's own (much smaller) scroll-scrub logic stays in the
 * parent component since it shares little with this beyond `isMultiLineMode`.
 */
export function useMultiRowSheetMusic(options: MultiRowSheetMusicOptions) {
  const {
    sheetViewportEl,
    measureBoundsForScroll,
    activeRenderedNotes,
    multiScale,
    canSeek,
    onSeek,
    refreshSheetGeometry,
    syncRenderedNotesIntoGameNotes,
  } = options;

  const sheetMusic = useInjectSheetMusic();
  const gameEngine = useInjectGameEngine();
  const { zoom, sheetMusicRenderMode } = useSettings();

  const isMultiLineMode = computed(() => sheetMusicRenderMode.value === 'multi');
  const isSwitchingRenderMode = ref(false);
  const modeButtonLabel = computed(() => (
    isMultiLineMode.value ? 'Switch to scrolling line view' : 'Switch to sheet page view'
  ));
  const modeIconName = computed(() => (
    isMultiLineMode.value ? 'solar:arrow-right-from-line-bold' : 'solar:list-arrow-down-bold'
  ));

  // The first row's content-relative y is ~0 (nothing above it to scroll into), so
  // centering it would need a *negative* scrollTop — impossible on a real scroll
  // container, which is why it always landed pinned to the top. Symmetrically,
  // the last row's y sits near the bottom of the content with nothing rendered
  // below it, so centering it would need a scrollTop past scrollHeight —
  // impossible too, clamping the last row pinned to the bottom instead of
  // centred. Reserving this much blank space both above and below the notation
  // gives the centering math actual room to scroll into at either end. Sized
  // proportionally to the screen (see recenterAfterLoad), not a fixed px
  // value, which safely covers the largest margin `getCenteredRowScrollTop`
  // could ever need.
  const multiSpacerPx = ref(0);
  const multiAutoFollowLocked = ref(true);
  const multiPendingUserScroll = ref(false);
  const multiProgrammaticScroll = ref(false);
  const multiInteractionWasPlaying = ref(false);
  const multiLastCenteredRowGroup = ref<number | null>(null);

  const multiRows = computed<MultiRow[]>(() => {
    if (!isMultiLineMode.value) return [];

    const groupedRows = new Map<number, {
      bounds: MeasureBound[];
      notes: Note[];
      measureStartIndex: number;
      measureEndIndex: number;
    }>();

    measureBoundsForScroll.value.forEach((bound) => {
      if (bound.rowGroup === undefined || bound.y === undefined || bound.height === undefined) return;
      const existing = groupedRows.get(bound.rowGroup);
      if (existing) {
        existing.bounds.push(bound);
        existing.measureStartIndex = Math.min(existing.measureStartIndex, bound.measureIndex);
        existing.measureEndIndex = Math.max(existing.measureEndIndex, bound.measureIndex);
        return;
      }
      groupedRows.set(bound.rowGroup, {
        bounds: [bound],
        notes: [],
        measureStartIndex: bound.measureIndex,
        measureEndIndex: bound.measureIndex,
      });
    });

    activeRenderedNotes().forEach((note) => {
      const bound = getMeasureBoundForIndex(measureBoundsForScroll.value, note.measureIndex);
      if (bound?.rowGroup === undefined) return;
      groupedRows.get(bound.rowGroup)?.notes.push(note);
    });

    return [...groupedRows.entries()]
      .sort((a, b) => a[0] - b[0])
      .reduce<MultiRow[]>((rows, [rowGroup, row]) => {
        const rowNotes = [...row.notes].sort((a, b) => a.timestamp - b.timestamp);
        const minX = Math.min(...row.bounds.map((bound) => bound.x));
        const maxX = Math.max(...row.bounds.map((bound) => bound.x + bound.width));

        // Row vertical placement is the union of its own measures' bounds
        // (computeOsmdMeasureBounds already unions each measure's staff top/
        // height with its content, so this covers the full staff regardless
        // of which pitches are actually played) — not derived from the row's
        // own notes: a row where every note shares one line/space (e.g. an
        // all-hi-hat passage) would otherwise collapse the band to a sliver
        // around that one pitch, well short of the real staff, making the
        // target-line marker look like it's floating above the row instead
        // of spanning it. Bonus — well, almost: in multi-line mode these bounds
        // are measured off sheetMusicDiv itself (see recomputeMeasureBounds),
        // just like note.y, so row.y needs that same multiSpacerPx correction
        // added by hand before it lands in the scrollable-viewport's frame
        // (which every other row.y consumer below assumes it's already in).
        const minBoundY = Math.min(...row.bounds.map((bound) => bound.y ?? 0));
        const maxBoundY = Math.max(...row.bounds.map((bound) => (bound.y ?? 0) + (bound.height ?? 0)));
        const y = minBoundY + multiSpacerPx.value;
        const height = maxBoundY - minBoundY;

        rows.push({
          rowGroup,
          x: minX,
          y,
          width: maxX - minX,
          height,
          measureStartIndex: row.measureStartIndex,
          measureEndIndex: row.measureEndIndex,
          noteStartMs: sheetMusic.getMeasureStartMs(row.measureStartIndex) ?? rowNotes[0]?.timestamp ?? 0,
          noteEndMs: rowNotes.at(-1)?.timestamp
            ?? sheetMusic.getMeasureStartMs(row.measureEndIndex + 1)
            ?? sheetMusic.songDuration.value,
          // When the row's last note actually finishes sounding — the start
          // of the next measure (i.e. the end of this row's own last
          // measure), not just the last note's onset. Used to decide how long
          // the target line keeps riding out this row before jumping to the
          // next one: it should stay exactly as long as that last note lasts.
          soundEndMs: sheetMusic.getMeasureStartMs(row.measureEndIndex + 1)
            ?? rowNotes.at(-1)?.timestamp
            ?? sheetMusic.songDuration.value,
          notes: rowNotes,
        });

        return rows;
      }, []);
  });

  const activeMultiRow = computed<MultiRow | null>(() => {
    const rows = multiRows.value;
    if (!rows.length) return null;

    const currentTime = gameEngine.currentTime.value;
    for (const row of rows) {
      if (currentTime <= row.noteEndMs) return row;
    }

    return rows.at(-1) ?? null;
  });

  const rawMarkerXForRow = (row: MultiRow, currentTime: number): number => {
    if (!row.notes.length) return row.x;

    const position = computeSheetScrollPosition(
      row.notes,
      currentTime,
      row.x,
      row.x,
    );

    return position ? row.x - position.scrollOffset : row.x;
  };

  // How the marker crosses from one row's last note into the next row's
  // first. Two things drive this, both read straight off the row/note data
  // instead of invented constants:
  //  1. How long it stays on the outgoing row: exactly as long as that row's
  //     last note actually lasts — through the end of its last measure
  //     (`soundEndMs`), not just the note's onset. `rawMarkerXForRow` already
  //     keeps it moving smoothly through that whole window, since
  //     computeSheetScrollPosition extrapolates past a note's onset at its
  //     trailing speed (getTrailingNoteSpeed) rather than stopping dead.
  //  2. What happens once it jumps to the new row: it keeps moving at that
  //     same trailing speed (not a fresh speed computed from the new row, and
  //     not an eased curve) in a straight line until the new row's own first
  //     note is due, landing on it at exactly its timestamp. The row switch
  //     itself is driven purely by currentTime vs. soundEndMs — independent of
  //     however long the viewport's own scroll-into-view tween happens to
  //     take (see snapActiveMultiRowIntoView); the two aren't coupled.
  const multiMarkerDisplay = computed<{ row: MultiRow; x: number } | null>(() => {
    const rows = multiRows.value;
    if (!rows.length) return null;

    const currentTime = gameEngine.currentTime.value;
    // Strict "<" (except on the last row) so a currentTime that lands exactly
    // on a row boundary — e.g. seeking to a measure that's the very first of
    // a row, whose timestamp equals the previous row's soundEndMs by
    // definition — resolves to the new row starting, not the old one still
    // "ending" at that same instant.
    let activeIndex = rows.findIndex((row, i) => (
      i === rows.length - 1 ? currentTime <= row.soundEndMs : currentTime < row.soundEndMs
    ));
    if (activeIndex === -1) activeIndex = rows.length - 1;

    const activeRow = rows[activeIndex];
    const previousRow = activeIndex > 0 ? rows[activeIndex - 1] : null;
    const firstNote = activeRow.notes[0];

    if (previousRow && firstNote && currentTime < firstNote.timestamp) {
      const trailingSpeed = previousRow.notes.length
        ? getTrailingNoteSpeed(previousRow.notes)
        : 0;
      const x = firstNote.x - trailingSpeed * (firstNote.timestamp - currentTime);
      return { row: activeRow, x };
    }

    const x = clamp(
      rawMarkerXForRow(activeRow, currentTime),
      activeRow.x,
      activeRow.x + activeRow.width,
    );
    return { row: activeRow, x };
  });

  const multiMarkerRow = computed<MultiRow | null>(() => multiMarkerDisplay.value?.row ?? null);
  const multiMarkerX = computed<number | null>(() => multiMarkerDisplay.value?.x ?? null);

  const multiMarkerStyle = computed(() => {
    const row = multiMarkerRow.value;
    const markerX = multiMarkerX.value;
    if (!row || markerX === null) {
      return {
        left: '0px',
        top: '0px',
        height: '0px',
      };
    }

    const marginPx = MULTI_MARKER_VERTICAL_MARGIN_PX * multiScale();

    return {
      left: `${markerX}px`,
      // row.y is already relative to sheetViewportEl's own content origin (see
      // multiRows' doc comment) — same frame this marker renders into — so,
      // unlike row-centering math elsewhere that works in note-space, it must
      // NOT have multiSpacerPx added again here.
      top: `${row.y - marginPx}px`,
      height: `${row.height + marginPx * 2}px`,
      width: `${MULTI_MARKER_LINE_WIDTH_PX * multiScale()}px`,
    };
  });

  const multiMarkerArrowStyle = computed(() => {
    const scale = multiScale();
    const halfWidth = MULTI_MARKER_ARROW_HALF_WIDTH_PX * scale;
    const lineWidth = MULTI_MARKER_LINE_WIDTH_PX * scale;
    return {
      left: `${-(halfWidth * 2 - lineWidth) / 2}px`,
      borderLeftWidth: `${halfWidth}px`,
      borderRightWidth: `${halfWidth}px`,
    };
  });

  const multiMarkerArrowTopStyle = computed(() => ({
    ...multiMarkerArrowStyle.value,
    borderTopWidth: `${MULTI_MARKER_ARROW_HEIGHT_PX * multiScale()}px`,
  }));

  const multiMarkerArrowBottomStyle = computed(() => ({
    ...multiMarkerArrowStyle.value,
    borderBottomWidth: `${MULTI_MARKER_ARROW_HEIGHT_PX * multiScale()}px`,
  }));

  const showMultiMarker = computed(() => (
    isMultiLineMode.value
    && sheetMusic.isLoaded.value
    && gameEngine.targetLineVisible.value
    && !!activeMultiRow.value
    && multiMarkerX.value !== null
  ));

  const getCenteredRowScrollTop = (row: MultiRow) => {
    const viewport = sheetViewportEl.value;
    if (!viewport) return 0;
    const maxScroll = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
    const availableHeight = Math.max(0, viewport.clientHeight - MULTI_HUD_GUTTER_PX);
    const margin = Math.max(0, (availableHeight - row.height) / 2);
    // row.y is already relative to the scrollable viewport's own content
    // origin (see multiRows' doc comment — it's a union of measure bounds
    // computed off sheetViewportEl), the same frame scrollTop/maxScroll live
    // in, so it needs no further adjustment before comparing against them.
    const centered = row.y - MULTI_HUD_GUTTER_PX/2 - margin;
    return clamp(centered, 0, maxScroll);
  };

  const snapActiveMultiRowIntoView = (animate: boolean) => {
    const viewport = sheetViewportEl.value;
    const row = activeMultiRow.value;
    if (!viewport || !row) return;

    const targetScrollTop = getCenteredRowScrollTop(row);
    gsap.killTweensOf(viewport);

    if (!animate) {
      multiProgrammaticScroll.value = true;
      viewport.scrollTop = targetScrollTop;
      requestAnimationFrame(() => {
        multiProgrammaticScroll.value = false;
      });
      return;
    }

    multiProgrammaticScroll.value = true;
    gsap.to(viewport, {
      scrollTop: targetScrollTop,
      duration: 0.35,
      ease: 'power3.out',
      overwrite: 'auto',
      onComplete: () => {
        multiProgrammaticScroll.value = false;
      },
      onInterrupt: () => {
        multiProgrammaticScroll.value = false;
      },
    });
  };

  /**
   * Sizes the top/bottom spacers (the space that lets any row scroll to the
   * centre) and waits for that to land in the DOM. Row bounds are measured in
   * the viewport's content frame, which the spacer pushes down, so measure
   * only after this resolves. No-op in single-line mode.
   */
  const commitMultiSpacer = async () => {
    if (!isMultiLineMode.value) return;

    // scrollReady only just flipped true in the caller, which drops the
    // `absolute opacity-0` classes that keep the viewport out of flow while
    // hidden — on the very first load that's a real layout change, so the
    // viewport's clientHeight isn't reliable until Vue has flushed it to the
    // DOM.
    await nextTick();
    if (sheetViewportEl.value) {
      // Floor against window.innerHeight rather than a flat px value — keeps
      // the reserved space (and thus how far a row must be able to scroll to
      // reach centre) proportional to the actual screen size instead of a
      // one-size-fits-all guess.
      const minSpacerPx = window.innerHeight / 2;
      multiSpacerPx.value = Math.max(minSpacerPx, sheetViewportEl.value.clientHeight / 2);
    }
    // The spacer's rendered height isn't in the DOM until Vue flushes it.
    await nextTick();
  };

  /**
   * Re-centers on the current row after a full geometry refresh (initial load,
   * note-set change, zoom change, mode switch). Expects commitMultiSpacer and
   * a fresh measure of the row bounds to have happened first. No-op in
   * single-line mode.
   */
  const recenterAfterLoad = () => {
    if (!isMultiLineMode.value) return;

    multiAutoFollowLocked.value = true;
    snapActiveMultiRowIntoView(false);
    multiLastCenteredRowGroup.value = activeMultiRow.value?.rowGroup ?? null;
  };

  watch(() => activeMultiRow.value?.rowGroup, (rowGroup) => {
    if (!isMultiLineMode.value || rowGroup === undefined || rowGroup === null || !multiAutoFollowLocked.value) return;

    const shouldAnimate = gameEngine.isPlaying.value
      && multiLastCenteredRowGroup.value !== null
      && multiLastCenteredRowGroup.value !== rowGroup;

    snapActiveMultiRowIntoView(shouldAnimate);
    multiLastCenteredRowGroup.value = rowGroup;
  });

  watch(() => gameEngine.isPlaying.value, (isPlaying) => {
    if (!isMultiLineMode.value || !isPlaying) return;
    multiAutoFollowLocked.value = true;
    multiPendingUserScroll.value = false;
    multiInteractionWasPlaying.value = false;
    snapActiveMultiRowIntoView(false);
    multiLastCenteredRowGroup.value = activeMultiRow.value?.rowGroup ?? null;
  });

  watch(zoom, async (nextZoom, previousZoom) => {
    if (nextZoom === previousZoom || !isMultiLineMode.value || !sheetMusic.isLoaded.value) return;
    sheetMusic.renderOsmd(multiScale());
    await nextTick();
    // A zoom-only render() (no full load()) can still rewrap which measures land on
    // which row once the content crosses the container's width at the new scale —
    // and OSMD rebuilds its graphical objects when that happens. Each Note's cached
    // `graphicalNote` (captured once by processNotes() at load time) then points at
    // a stale/orphaned object whose SVG rect no longer reflects the new layout, which
    // is what caused row Y positions to drift specifically past whatever zoom first
    // triggers a rewrap. Re-resolving graphicalNote fresh via processNotes() (cheap —
    // it walks the already-parsed Sheet, no XML re-parse) keeps every note's rect
    // reference current after any re-render, not just the first.
    await sheetMusic.processNotes();
    syncRenderedNotesIntoGameNotes();
    await refreshSheetGeometry();
  });

  // Manual-scroll interactions (drag/wheel) take over from auto-follow and
  // pause playback — gated by canSeek the same as click-to-seek
  // (handleMultiMeasureClick), so a view-only participant (e.g. a classroom
  // student) can't pause/derail the song just by touching or scrolling the
  // sheet. For them, the viewport itself is also `overflow-hidden` (see
  // GameAreaSheetMusic.vue), so these handlers are defense-in-depth: the
  // browser never generates the wheel/scroll gestures that would reach them.
  const handleMultiPointerDown = (event: PointerEvent) => {
    if (!canSeek()) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    multiPendingUserScroll.value = true;
    multiInteractionWasPlaying.value = gameEngine.isPlaying.value;
    if (gameEngine.isPlaying.value) {
      gameEngine.stopPlayback();
    }
  };

  const handleMultiPointerUp = () => {
    window.setTimeout(() => {
      multiPendingUserScroll.value = false;
      multiInteractionWasPlaying.value = false;
    }, 0);
  };

  const handleMultiWheel = () => {
    if (!canSeek()) return;
    multiPendingUserScroll.value = true;
    multiInteractionWasPlaying.value = gameEngine.isPlaying.value;
    if (gameEngine.isPlaying.value) {
      gameEngine.stopPlayback();
    }
  };

  const handleMultiScroll = () => {
    if (!sheetViewportEl.value) return;
    // Same canSeek gate as the other three handlers above — without it, a
    // student whose touch gesture began (pointerdown) a moment before canSeek
    // flipped false, or any other stray path that left multiPendingUserScroll
    // set, could still unlock auto-follow and desync their view here even
    // though the handler that would've set that flag is itself gated.
    if (!canSeek()) {
      multiPendingUserScroll.value = false;
      return;
    }
    if (!multiProgrammaticScroll.value && multiPendingUserScroll.value) {
      multiAutoFollowLocked.value = false;
      multiInteractionWasPlaying.value = false;
    }
  };

  const handleMultiMeasureClick = (event: MouseEvent) => {
    multiPendingUserScroll.value = false;
    if (!canSeek() || !sheetViewportEl.value || !sheetMusic.isLoaded.value) return;

    const target = event.target as HTMLElement | null;
    if (target?.closest('.note-annotation')) return;

    const measure = getMeasureAtPoint(
      measureBoundsForScroll.value,
      sheetViewportEl.value.getBoundingClientRect(),
      event.clientX,
      event.clientY,
      {
        scrollLeft: sheetViewportEl.value.scrollLeft,
        scrollTop: sheetViewportEl.value.scrollTop - multiSpacerPx.value,
      },
    );

    if (!measure) return;

    const timeMs = sheetMusic.getMeasureStartMs(measure.measureIndex);
    if (timeMs === undefined) return;

    const wasPlaying = multiInteractionWasPlaying.value || gameEngine.isPlaying.value;
    if (gameEngine.isPlaying.value) {
      gameEngine.stopPlayback();
    }

    // false: this seek doesn't resume playback (see handleSeek upstream, which
    // stops it), so seekTo's default metronome-lookahead backdating would just
    // sit frozen 50ms before the clicked measure — enough to misclassify which
    // row/line the clicked measure's own start belongs to. Land exactly on it,
    // same rationale as the loop-wrap case in seekTo's doc comment.
    gameEngine.seekTo(timeMs, false);
    multiAutoFollowLocked.value = true;
    snapActiveMultiRowIntoView(false);
    multiLastCenteredRowGroup.value = activeMultiRow.value?.rowGroup ?? null;
    multiInteractionWasPlaying.value = false;

    onSeek({ timeMs, wasPlaying });
  };

  const toggleRenderMode = async () => {
    if (isSwitchingRenderMode.value) return;

    const nextMode = isMultiLineMode.value ? 'single' : 'multi';
    sheetMusicRenderMode.value = nextMode;

    if (!sheetMusic.osmd.value || !sheetMusic.xmlContent.value) return;

    isSwitchingRenderMode.value = true;
    const previousMode = nextMode === 'multi' ? 'single' : 'multi';

    try {
      await nextTick();
      const switched = await sheetMusic.setRenderMode(nextMode, nextMode === 'multi' ? multiScale() : 1);
      if (!switched) {
        sheetMusicRenderMode.value = previousMode;
        return;
      }

      syncRenderedNotesIntoGameNotes();
      await refreshSheetGeometry();
    } finally {
      isSwitchingRenderMode.value = false;
    }
  };

  return {
    isMultiLineMode,
    isSwitchingRenderMode,
    modeButtonLabel,
    modeIconName,
    multiSpacerPx,
    multiMarkerStyle,
    multiMarkerArrowTopStyle,
    multiMarkerArrowBottomStyle,
    showMultiMarker,
    handleMultiPointerDown,
    handleMultiPointerUp,
    handleMultiWheel,
    handleMultiScroll,
    handleMultiMeasureClick,
    toggleRenderMode,
    commitMultiSpacer,
    recenterAfterLoad,
  };
}
